import { generateDateTime } from '../../../utils/generatedDateTime';

// Shared trial helpers used by both the runSession slice (thunks/reducers) and TargetCard.
// They live here so the fetch/save paths can never diverge on phase naming or response shaping.

export const TIMER_TARGET_TYPES = ['duration', 'latency', 'time sampling', 'rate'];

// The two types rendered by DurationTrialCard, where a trial's value is a stopwatch reading.
export const DURATION_TARGET_TYPES = ['duration', 'latency'];

// Trials are stored/looked up under a phase name that includes the mastery-criteria
// phase index when there is one (e.g. a second Intervention/Maintenance block becomes
// "Intervention2"). Both the save and the reload MUST use this same value, otherwise
// data saved under "Intervention2" is never returned for a plain "Intervention" query.
export function getEffectivePhase(phase: string, target: any): string {
  const idx = target?.masteryCriteriaPhaseIndexCount;
  if (idx !== undefined && idx !== null) {
    return `${phase}`;
  }
  return phase;
}

// Cache key for state.runSession.value.trials.
//
// `recordDate`/`editMode` are deliberately NOT part of the key: getTrialsByTarget accepts
// exactly { sessionRunId, targetId, phase }, so those two never change the response body —
// they only gate whether we fetch at all and how currentTrial is derived. The day the
// endpoint starts accepting recordDate, it has to be added here too.
export function makeTrialKey(sessionRunId: string, phase: string, target: any): string {
  return `${sessionRunId}|${getEffectivePhase(phase, target)}|${target?.id}`;
}

export function isTrialKeyForRun(key: string, sessionRunId: string): boolean {
  return key.startsWith(`${sessionRunId}|`);
}

// Same run, same target, a different phase tab's cache entry.
//
// A pinned target is collected throughout the session and the backend returns its trials
// whatever phase is queried, but each tab still caches the response under its own key. So a
// save made on one tab leaves the other tabs' entries holding the value from before it —
// which is why the same pinned counter read differently depending on the tab. Used to copy
// a save across those sibling entries.
export function isSiblingPhaseKey(key: string, ownKey: string): boolean {
  if (key === ownKey) return false;
  const [run, , targetId] = key.split('|');
  const [ownRun, , ownTargetId] = ownKey.split('|');
  return run === ownRun && targetId === ownTargetId;
}

// The cache entry of the same target under another phase tab that actually holds saved
// trials, if there is one.
//
// The trials endpoint filters by phase, so a phase the clinician has not written to yet
// returns [] even for a pinned target whose data is shared across every tab. Writing that []
// into the tab's entry blanked the card, and a blank card is indistinguishable from a fresh
// one: collectZero*Data re-fired and wrote a 0 over the count recorded under another tab.
export function findSiblingTrials(
  trials: Record<string, any>,
  ownKey: string
): { data: any[]; currentTrial: number } | undefined {
  const siblingKey = Object.keys(trials).find(
    key =>
      isSiblingPhaseKey(key, ownKey) &&
      trials[key]?.data?.some((row: any) => row?.trialId || row?.id)
  );
  return siblingKey ? trials[siblingKey] : undefined;
}

// Has this target been saved at least once anywhere in this run, whatever the phase?
//
// A pinned target is collected once for the whole session, so this is the answer to "has the
// automatic zero row already been collected" that survives a remount — a component ref does
// not, and the card remounts whenever the target list is refetched for another phase.
export function hasSavedTrialInRun(
  trials: Record<string, any>,
  sessionRunId: string,
  targetId: string
): boolean {
  return Object.keys(trials).some(key => {
    const [run, , target] = key.split('|');
    if (run !== `${sessionRunId}` || target !== `${targetId}`) return false;
    return trials[key]?.data?.some((row: any) => row?.trialId || row?.id);
  });
}

// A frequency target is a single running counter, not a series of trials: it always has
// exactly one trial row and every tap updates that same row.
export function isFrequencyTarget(target: any): boolean {
  return target?.targetType?.toLowerCase() === 'frequency';
}

// Legacy sessions can hold several rows for one frequency target — a save whose cache entry
// was empty (a phase whose fetch returned nothing) went out with an empty trialId and the
// backend minted another row. Collapse to the newest saved row so the counter has one value
// and every later save carries that row's trialId, updating it instead of appending again.
export function collapseFrequencyTrials(responseData: any[]): any[] {
  if (!responseData?.length) return [];
  const newestSaved = [...responseData].reverse().find((t: any) => t?.trialId || t?.id);
  return [newestSaved ?? responseData[responseData.length - 1]];
}

export function buildTaskAnalysisResponseObj(data: any[]): Record<number, any> {
  const responseObj: any = {};
  data.forEach((resp: any) => {
    const idx = resp.trailCount - 1;
    const step = { prompts: resp.prompts, step: resp.stepId.id };
    if (responseObj[idx]?.response) {
      responseObj[idx].response = [...responseObj[idx].response, step];
    } else {
      responseObj[idx] = { ...resp, response: [step] };
    }
  });
  return responseObj;
}

// First Probe Intervention/Maintenance allow only one trial per day unless the
// program/target enables "Allow multiple data collections per day". When enabled,
// the per-day cap is maxTrialPerDay (null/undefined => infinite, no upper bound).
// Returns undefined when the per-day rule does not apply (fall back to maxTrials).
export function getFirstProbePerDayMax(target: any, phase?: string): number | null | undefined {
  const targetType = target?.targetType?.toLowerCase();
  if (targetType !== 'first probe') return undefined;
  const isBaseline = phase === 'Baseline' || target?.targetStatus?.name === 'Baseline';
  if (isBaseline) return undefined;
  // Intervention/Maintenance: only one trial per day unless multiple collection is enabled.
  if (!target?.allowMultipleDataCollection) return 1;
  return target?.maxTrialPerDay ? Number(target?.maxTrialPerDay) : null;
}

// How many duration/latency trials the clinician actually recorded.
//
// `collectZeroDurationData` writes a 0-second row as soon as the session starts, so the target
// is represented in the data even when nothing is observed. That row is not a collected trial:
// counting it advanced the card to the next trial on the following phase's fetch, even though
// nobody had recorded anything.
//
// A manual recording can never be 0 — DurationTrialCard's stop button stays disabled while the
// timer reads 0 — so a trailing zero-duration row is always the auto-collected one.
export function countRecordedDurationTrials(responseData: any[]): number {
  let count = responseData.length;
  while (count > 0 && !(Number(responseData[count - 1]?.duration) > 0)) count -= 1;
  return count;
}

export function computeNextTrialCount(params: {
  targetType: string;
  dataLength: number;
  editMode?: boolean;
  target: any;
  interactedTrial: number | null;
  phase?: string;
}): number {
  const { targetType, dataLength, editMode, target, interactedTrial, phase } = params;
  if (interactedTrial !== null && TIMER_TARGET_TYPES.includes(targetType)) return interactedTrial;
  // One trial, always. This used to be `dataLength`, which parked the card on the last of
  // however many rows the target had accumulated — so the counter's value changed depending
  // on which row it happened to land on.
  if (targetType === 'frequency') return 1;
  if (targetType === 'time sampling') {
    return editMode || dataLength === target?.timeSamplingIntervals ? dataLength : dataLength + 1;
  }
  if (targetType === 'first probe') {
    const perDayMax = getFirstProbePerDayMax(target, phase);
    if (perDayMax !== undefined) {
      // Multiple-per-day enabled: advance to the next empty slot up to the cap.
      const cappedAtMax = perDayMax !== null && dataLength === perDayMax;
      return editMode || cappedAtMax ? dataLength : dataLength + 1;
    }
  }
  // `maxTrials` is 0/undefined for an unlimited target, so it only counts as a cap when set —
  // otherwise a target with nothing collected yet matched `0 === 0` and reported trial 0.
  // Trials are 1-based, so the answer can never be below 1 either.
  const cappedAtMax = !!target?.maxTrials && dataLength === target.maxTrials;
  return Math.max(1, editMode || cappedAtMax ? dataLength : dataLength + 1);
}

export function computeCumulativeCounts(trials: any[]): { positive: number; negative: number } {
  let positive = 0;
  let negative = 0;
  trials.forEach((t: any) => {
    if (t?.trialId || t?.id) {
      positive += parseInt(t?.positiveCount) || 0;
      negative += parseInt(t?.negativeCount) || 0;
    }
  });
  return { positive, negative };
}

export function computeCanNavigateForward(params: {
  targetType: string;
  editMode?: boolean;
  currentTrial: number;
  trials: any[];
  maxTrials?: number;
  lastRecordedIndex: number;
  firstProbePerDayMax?: number | null;
}): boolean {
  const { targetType, editMode, currentTrial, trials, maxTrials, lastRecordedIndex } = params;
  // Nowhere to navigate to: a frequency target has one trial by definition.
  if (targetType === 'frequency') return false;
  if (targetType === 'opportunities') {
    if (maxTrials && currentTrial >= maxTrials) return false;
    return lastRecordedIndex >= 0 && currentTrial <= lastRecordedIndex + 1;
  }
  // First Probe (Intervention/Maintenance): the per-day cap governs navigation,
  // not maxTrials. null => infinite (no upper bound). Applies in live and edit mode.
  if (targetType === 'first probe' && params.firstProbePerDayMax !== undefined) {
    // If trial 1 is IND (positive, no error), no subsequent trials should be collected.
    const trial1 = trials[0];
    if (
      trial1 &&
      (trial1.trialId || trial1.id) &&
      parseInt(trial1.positiveCount) > 0 &&
      !parseInt(trial1.negativeCount)
    ) {
      return false;
    }
    if (params.firstProbePerDayMax !== null && currentTrial >= params.firstProbePerDayMax) {
      return false;
    }
    if (!editMode) return true;
    return !!trials[currentTrial]?.id;
  }
  if (!editMode) return true;
  if (maxTrials && currentTrial >= maxTrials) return false;
  return !!trials[currentTrial]?.id;
}

// ── Response shaping ─────────────────────────────────────────────────────────
// Pure transforms of an API response into { data, currentTrial }. Kept out of the
// thunk body so they can be unit-tested without mocking axios.

export interface ShapedTrials {
  data: any[];
  currentTrial: number;
}

export function shapeTaskAnalysisTrials(params: {
  responseData: any[];
  phase: string;
  editMode?: boolean;
}): ShapedTrials {
  const { responseData, phase, editMode } = params;
  if (!responseData?.length) {
    return { data: [], currentTrial: 1 };
  }
  const trialsData = Object.values(buildTaskAnalysisResponseObj(responseData));
  const currentTrial = (() => {
    if (phase === 'Baseline') return 1;
    if (editMode) return trialsData.length;
    return trialsData.length + 1;
  })();
  return { data: trialsData, currentTrial };
}

export function shapeRegularTrials(params: {
  responseData: any[];
  target: any;
  phase: string;
  editMode?: boolean;
  interactedTrial: number | null;
}): ShapedTrials {
  const { responseData, target, phase, editMode, interactedTrial } = params;
  if (!responseData?.length) {
    return { data: [], currentTrial: 1 };
  }
  const targetType = target?.targetType?.toLowerCase();
  let data = [...responseData];
  if (isFrequencyTarget(target)) {
    return { data: collapseFrequencyTrials(responseData), currentTrial: 1 };
  }
  if (targetType === 'first probe') {
    // First probe renders a trailing empty slot so the next probe can be recorded.
    const lastResponse = data[data.length - 1];
    data = [...data, { ...lastResponse, trialId: '' }];
  }
  // The rows stay as they are — an auto-collected zero row is still trial 1's value and must
  // render. It just does not count towards "how many trials have been collected".
  const dataLength = DURATION_TARGET_TYPES.includes(targetType)
    ? countRecordedDurationTrials(responseData)
    : responseData.length;
  const currentTrial = computeNextTrialCount({
    targetType,
    dataLength,
    editMode,
    target,
    interactedTrial,
    phase,
  });
  return { data, currentTrial };
}

// ── Save payload ─────────────────────────────────────────────────────────────

export interface TrialSaveDecoration {
  recordDate?: string;
  ranBy?: string;
  editMode?: boolean;
}

// Manual data insertion (Add Data / edit mode) stamps the record date and audit fields
// onto every save. Extracted from TargetCard so the save thunk owns the whole payload.
export function decorateTrialPayload(trial: any, decoration: TrialSaveDecoration): any {
  const { recordDate, ranBy, editMode } = decoration;
  if (!recordDate) return { ...trial };
  return {
    ...trial,
    modifiedDate: generateDateTime(recordDate),
    isManualDataInsertion: true,
    ranBy,
    editingManually: editMode,
  };
}
